/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import { type RefObject, useCallback, useEffect, useContext } from 'react';

import type { CKEditor5Props } from '../../types/CKEditor5Props';
import type { PresetView } from '../../utils/getPresets/getPresets';
import { type GTMEvent } from '../../utils/triggerGTMEvent/triggerGTMEvent';
import { GTMContext } from '../../contexts/GTMContext';
import { isObject } from 'lodash';

type useIframePreviewProps = Omit<CKEditor5Props, 'capabilities' | 'isSelectedPresetModified'> & {
	preset: PresetView;
	isIframeLoaded: boolean;
	locationOrigin: string;
	iframeRef: RefObject<HTMLIFrameElement>;
	windowHeight: number;
	onEditorDimensionsChange: ( newDimensions: { editorWidth: number; editorHeight: number } ) => void;
	onCKEditorError: ( error: Error, details: unknown ) => void;
	onFullscreenChange: ( isFullscreen: boolean ) => void;
};

type EventData = EventErrorData | {
	isCKEditor5BuilderEvent: boolean;
	editorWidth: number;
	editorHeight: number;
	eventType: 'windowHeightChange' | 'editorDataChange';
} | {
	isGTMEventTrigger: boolean;
	GTMEvent: GTMEvent;
} | {
	isFullscreenEvent: boolean;
	isFullscreen: boolean;
};

type PostMessageEvent = {
	data: unknown | EventData;
	origin: string;
};

type EventErrorData = {
	isCKEditor5BuilderEvent: boolean;
	error: Error;
	details: Record<string, unknown>;
};

function isSpecifiedObject( data: PostMessageEvent[ 'data' ] ): data is EventData {
	return isObject( data ) && !Array.isArray( data );
}

function isErrorEventData( data: EventData ): data is EventErrorData {
	return 'error' in data;
}

export function useAppToIframeSynchronization( {
	toolbarOptions,
	isIframeLoaded,
	iframeRef,
	locationOrigin,
	editorType,
	editorLanguage,
	editorPlugins,
	preset,
	windowHeight,
	onEditorDimensionsChange,
	onCKEditorError,
	onFullscreenChange
}: useIframePreviewProps ): void {
	const { triggerGTMEvent } = useContext( GTMContext );

	const handleMessage = useCallback( ( evt: PostMessageEvent ) => {
		const data = evt?.data;

		if ( !isSpecifiedObject( data ) ) {
			return;
		}

		if ( evt.origin !== locationOrigin ) {
			return;
		}

		if ( 'isGTMEventTrigger' in data && data.isGTMEventTrigger ) {
			triggerGTMEvent( data.GTMEvent );
		}

		if ( 'isFullscreenEvent' in data && data.isFullscreenEvent ) {
			onFullscreenChange( data.isFullscreen );
		}

		if ( 'isCKEditor5BuilderEvent' in data && data.isCKEditor5BuilderEvent ) {
			if ( isErrorEventData( data ) ) {
				onCKEditorError( data.error, data.details );
			} else {
				const { editorWidth, editorHeight } = data;

				onEditorDimensionsChange( { editorHeight, editorWidth } );
			}
		}
	}, [ locationOrigin, onEditorDimensionsChange, onCKEditorError, onFullscreenChange, triggerGTMEvent ] );

	useEffect( () => {
		const eventListenerArgs = [ 'message', handleMessage, false ] as const;

		window.addEventListener( ...eventListenerArgs );

		return () => window.removeEventListener( ...eventListenerArgs );
	}, [ handleMessage ] );

	useEffect( () => {
		if ( isIframeLoaded ) {
			iframeRef.current?.contentWindow?.postMessage(
				{
					editorType,
					editorLanguage,
					editorPlugins,
					presetName: preset.name,
					toolbarOptions,
					isCKEditor5BuilderEvent: true,
					eventType: 'editorDataChange'
				},
				locationOrigin
			);
		}
	}, [ editorType, editorLanguage, editorPlugins, preset, toolbarOptions, locationOrigin, iframeRef, isIframeLoaded ] );

	useEffect( () => {
		if ( isIframeLoaded ) {
			iframeRef.current?.contentWindow?.postMessage(
				{ windowHeight, isCKEditor5BuilderEvent: true, eventType: 'windowHeightChange' },
				locationOrigin
			);
		}
	}, [ windowHeight, iframeRef, locationOrigin, isIframeLoaded ] );
}
